import type {
  ClaimMapping,
  Delegation,
  DelegationRule,
  DelegationView,
  EntitlementRow,
  IdentityProviderCard,
  PermissionRow,
  PersonRef,
  ProfileView,
  QueryMetadata,
  QueryResult,
  RoleAssignment,
  RoleDefinition,
  RolesAccessView,
  ScopeRef,
  SignInEvent,
  SignInView,
} from '@aurora-pvi/contracts';
import type { RepositoryContext } from './context';
import { resolved } from './context';
import type { SeededPersona } from './types';

/**
 * Illustrative seed data for the identity screens, and the repository over it.
 *
 * Named `identity-access` rather than `identity` because `identity.ts` next to
 * it already holds `SEEDED_PERSONAS` — the people you can sign in as, which is a
 * different question from what those people are allowed to do.
 *
 * Everything here is deliberately consistent with `SEEDED_PERSONAS` — the same six
 * people, the same scope codes, the same entitlement flags — so switching
 * identity in the top bar changes these screens for the same reason it changes
 * every other one, rather than for a parallel reason invented here.
 *
 * The dataset has a fixed clock. `IDENTITY_AS_OF` is the dataset's day, not the
 * reader's, so "expires in 4 days" stays true on a Monday in March; every date
 * comparison on these screens goes through it.
 */

const IDENTITY_AS_OF = '2026-07-31';
const REFRESHED_AT = '2026-07-31T04:20:00Z';

// ── People ───────────────────────────────────────────────────────────────────

function person(
  code: string,
  displayName: string,
  jobTitle: string,
  orgUnitLabel: string,
): PersonRef {
  return { id: `identity-${code}`, code, displayName, jobTitle, orgUnitLabel };
}

const PEOPLE = {
  ingrid: person(
    'PPL-0001',
    'Ingrid Halvorsen',
    'Chief Information Officer',
    'Aurora Airlines — enterprise',
  ),
  nadia: person(
    'PPL-0007',
    'Nadia Benali',
    'Head of Data Governance',
    'Aurora Airlines — enterprise',
  ),
  julia: person(
    'PPL-0015',
    'Julia Sandberg',
    'VP, Network & Operations Technology',
    'Network & Operations',
  ),
  maria: person('PPL-0020', 'Maria Alvarez', 'Senior Director, Crew Operations', 'Crew Operations'),
  tomas: person(
    'PPL-0031',
    'Tomas Lindqvist',
    'Director, Network Planning',
    'Network & Operations',
  ),
  simone: person(
    'PPL-0046',
    'Simone Adeyemi',
    'Product Owner, Crew Disruption Recovery',
    'Crew Operations',
  ),
  louis: person(
    'PPL-0053',
    'Louis Ferrand',
    'Domain Architect, Flight Operations',
    'Network & Operations',
  ),
  priya: person('PPL-0064', 'Priya Raman', 'Lead, Revenue Management', 'Commercial'),
  anders: person(
    'PPL-0072',
    'Anders Vik',
    'Platform Administrator',
    'Aurora Airlines — enterprise',
  ),
} as const;

// ── Scopes ───────────────────────────────────────────────────────────────────

const SCOPES = {
  enterprise: { code: 'ENT-AURORA', label: 'Aurora Airlines — enterprise', type: 'Enterprise' },
  nops: { code: 'DEP-NOPS', label: 'Network & Operations', type: 'Department' },
  comm: { code: 'DEP-COMM', label: 'Commercial', type: 'Department' },
  crew: { code: 'VS-CREW', label: 'Crew Operations', type: 'ValueStream' },
  network: { code: 'VS-NET', label: 'Network Planning', type: 'ValueStream' },
  cdr: { code: 'PRD-CDR', label: 'Crew Disruption Recovery', type: 'Product' },
} as const satisfies Record<string, ScopeRef>;

// ── Modules ──────────────────────────────────────────────────────────────────

/** Keys match `NAVIGATION[].items[].module` in contracts/src/routes.ts. */
const MODULE_LABELS: Record<string, string> = {
  portfolio: 'Portfolio & executive summary',
  'product-definition': 'Product definition & candidates',
  'product-360': 'Product 360',
  'relationship-mapping': 'Process & application mapping',
  application: 'Application portfolio',
  'delivery-reliability': 'Delivery & flow',
  value: 'Investment, workforce, suppliers, contracts',
  scenario: 'Scenario modelling',
  ingestion: 'Import, integrations, lineage',
  governance: 'Governance & administration',
};

const ALL_MODULES = Object.keys(MODULE_LABELS);

// ── Roles (UC-02-006) ────────────────────────────────────────────────────────

const ROLES: RoleDefinition[] = [
  {
    roleId: 'ROL-001',
    code: 'ROL-EXEC',
    name: 'Enterprise executive',
    purpose: 'Read the whole portfolio and take figures away, without changing any of them.',
    permissions: ['view', 'export'],
    sensitivity: 'Restricted',
    modules: ALL_MODULES,
    assigneeCount: 6,
    steward: PEOPLE.nadia,
    delegable: false,
    nonDelegableReason:
      'Enterprise-wide read carries restricted financial fields. Delegating it would widen the audience for those fields without an entitlement decision, so it is re-granted rather than delegated.',
    lastReviewedAt: '2026-06-12',
  },
  {
    roleId: 'ROL-002',
    code: 'ROL-PORT',
    name: 'Portfolio steward',
    purpose:
      'Maintain the product hierarchy, classifications and ownership within a scope, and approve changes inside it.',
    // Carries `approve`, which is what makes the role delegable in any useful
    // sense: the delegations in this seed are all somebody handing over an
    // approval, and a role without one would have nothing to hand over.
    permissions: ['view', 'create', 'edit', 'propose', 'approve', 'export'],
    sensitivity: 'Confidential',
    modules: ['portfolio', 'product-definition', 'product-360', 'relationship-mapping', 'value'],
    assigneeCount: 14,
    steward: PEOPLE.nadia,
    delegable: true,
    nonDelegableReason: null,
    lastReviewedAt: '2026-06-12',
  },
  {
    roleId: 'ROL-003',
    code: 'ROL-PROD',
    name: 'Product owner',
    purpose: 'Own one product: its definition, its applications and its delivery record.',
    permissions: ['view', 'create', 'edit', 'propose'],
    sensitivity: 'Confidential',
    modules: [
      'portfolio',
      'product-360',
      'relationship-mapping',
      'application',
      'delivery-reliability',
      'value',
    ],
    assigneeCount: 38,
    steward: PEOPLE.julia,
    delegable: true,
    nonDelegableReason: null,
    lastReviewedAt: '2026-05-29',
  },
  {
    roleId: 'ROL-004',
    code: 'ROL-ARCH',
    name: 'Domain architect',
    purpose: 'Map applications and processes to products and propose corrections to the map.',
    permissions: ['view', 'propose', 'export'],
    sensitivity: 'Internal',
    modules: [
      'portfolio',
      'product-definition',
      'product-360',
      'relationship-mapping',
      'application',
      'delivery-reliability',
    ],
    assigneeCount: 11,
    steward: PEOPLE.louis,
    delegable: true,
    nonDelegableReason: null,
    lastReviewedAt: '2026-06-12',
  },
  {
    roleId: 'ROL-005',
    code: 'ROL-GOV',
    name: 'Data governance lead',
    purpose: 'Approve definitions, resolve competing values and run the access review.',
    permissions: ['view', 'edit', 'approve', 'administer', 'export'],
    sensitivity: 'Confidential',
    modules: ALL_MODULES,
    assigneeCount: 4,
    steward: PEOPLE.ingrid,
    delegable: true,
    nonDelegableReason: null,
    lastReviewedAt: '2026-07-03',
  },
  {
    roleId: 'ROL-006',
    code: 'ROL-ADMIN',
    name: 'Platform administrator',
    purpose: 'Configure connectors, schedules and the platform itself. Not a data role.',
    permissions: ['view', 'administer'],
    sensitivity: 'Internal',
    modules: ['ingestion', 'governance'],
    assigneeCount: 3,
    // Deliberately unowned in the seed: an unowned administrator role is the
    // finding an access review is supposed to produce, and the screen shows it
    // as a gap rather than pretending someone signed for it.
    steward: null,
    delegable: false,
    nonDelegableReason:
      'Platform administration can change who else has access. Delegating it would let authority be widened by someone who was not granted the power to widen it.',
    lastReviewedAt: null,
  },
  {
    roleId: 'ROL-007',
    code: 'ROL-AUDIT',
    name: 'Auditor',
    purpose: 'Read every governance record and audit trail. Changes nothing, anywhere.',
    permissions: ['view', 'export'],
    sensitivity: 'Restricted',
    modules: ['governance', 'ingestion', 'portfolio'],
    assigneeCount: 2,
    steward: PEOPLE.nadia,
    delegable: false,
    nonDelegableReason:
      'Independence is the whole value of the role. A delegated auditor is chosen by the audited.',
    lastReviewedAt: '2026-07-03',
  },
];

const PERMISSION_ROWS: PermissionRow[] = [
  {
    permission: 'view',
    label: 'View',
    meaning: 'Read records inside the granted scope. Field-level masking still applies.',
    grantedTo: ROLES.map((role) => role.code),
  },
  {
    permission: 'create',
    label: 'Create',
    meaning: 'Add products, candidates and mappings as drafts.',
    grantedTo: ['ROL-PORT', 'ROL-PROD'],
  },
  {
    permission: 'edit',
    label: 'Edit',
    meaning: 'Change a draft or a published record that the identity owns.',
    grantedTo: ['ROL-PORT', 'ROL-PROD', 'ROL-GOV'],
  },
  {
    permission: 'propose',
    label: 'Propose',
    meaning: 'Submit a change for someone else to approve. Never self-approves.',
    grantedTo: ['ROL-PORT', 'ROL-PROD', 'ROL-ARCH'],
  },
  {
    permission: 'approve',
    label: 'Approve',
    meaning: 'Publish a proposed change. The one permission delegation is usually about.',
    grantedTo: ['ROL-PORT', 'ROL-GOV'],
  },
  {
    permission: 'administer',
    label: 'Administer',
    meaning: 'Change platform configuration, roles and access windows.',
    grantedTo: ['ROL-GOV', 'ROL-ADMIN'],
  },
  {
    permission: 'export',
    label: 'Export',
    meaning: 'Take data out of the platform. Exports inherit the caller’s masking.',
    grantedTo: ['ROL-EXEC', 'ROL-PORT', 'ROL-ARCH', 'ROL-GOV', 'ROL-AUDIT'],
  },
];

function assignment(
  assignmentId: string,
  personRef: PersonRef,
  roleCode: string,
  scope: ScopeRef,
  from: string,
  to: string | null,
  source: RoleAssignment['source'],
  extras: Partial<
    Pick<RoleAssignment, 'grantedBy' | 'grantedAt' | 'justification' | 'lastReviewedAt'>
  > &
    Partial<RoleAssignment['lifecycle']> = {},
): RoleAssignment {
  const role = ROLES.find((candidate) => candidate.code === roleCode);
  return {
    assignmentId,
    person: personRef,
    roleCode,
    roleName: role?.name ?? roleCode,
    scope,
    window: { from, to },
    lifecycle: {
      revokedAt: extras.revokedAt ?? null,
      requiresAcceptance: false,
      acceptedAt: null,
    },
    grantedBy: extras.grantedBy ?? PEOPLE.nadia,
    grantedAt: extras.grantedAt ?? `${from}T08:00:00Z`,
    source,
    justification: extras.justification ?? null,
    lastReviewedAt: extras.lastReviewedAt ?? null,
  };
}

const ASSIGNMENTS: RoleAssignment[] = [
  assignment(
    'ASG-0001',
    PEOPLE.ingrid,
    'ROL-EXEC',
    SCOPES.enterprise,
    '2024-01-08',
    null,
    'HR feed',
    {
      justification: 'Officer of the company; access follows the appointment.',
      lastReviewedAt: '2026-06-12',
    },
  ),
  assignment(
    'ASG-0002',
    PEOPLE.nadia,
    'ROL-GOV',
    SCOPES.enterprise,
    '2024-03-01',
    null,
    'HR feed',
    {
      grantedBy: PEOPLE.ingrid,
      justification: 'Accountable for the governance model across the enterprise.',
      lastReviewedAt: '2026-07-03',
    },
  ),
  assignment(
    'ASG-0003',
    PEOPLE.julia,
    'ROL-PORT',
    SCOPES.nops,
    '2024-09-16',
    null,
    'Access request',
    {
      grantedBy: PEOPLE.nadia,
      justification: 'Owns the Network & Operations technology portfolio.',
      lastReviewedAt: '2026-06-12',
    },
  ),
  assignment(
    'ASG-0004',
    PEOPLE.maria,
    'ROL-PORT',
    SCOPES.crew,
    '2025-04-07',
    null,
    'Access request',
    {
      grantedBy: PEOPLE.julia,
      justification: 'Crew Operations value stream lead.',
      lastReviewedAt: '2026-06-12',
    },
  ),
  assignment('ASG-0005', PEOPLE.simone, 'ROL-PROD', SCOPES.cdr, '2025-11-03', null, 'HR feed', {
    grantedBy: PEOPLE.maria,
    justification: 'Named product owner for Crew Disruption Recovery.',
  }),
  assignment('ASG-0006', PEOPLE.louis, 'ROL-ARCH', SCOPES.nops, '2025-02-17', null, 'HR feed', {
    grantedBy: PEOPLE.julia,
    // Deliberately null: the grant predates the justification requirement, and
    // the screen says that rather than inventing a reason after the fact.
    justification: null,
    lastReviewedAt: '2026-06-12',
  }),
  assignment(
    'ASG-0007',
    PEOPLE.tomas,
    'ROL-PROD',
    SCOPES.network,
    '2026-08-10',
    '2026-11-30',
    'Access request',
    {
      grantedBy: PEOPLE.julia,
      grantedAt: '2026-07-24T13:40:00Z',
      justification: 'Covering the winter schedule build while the seat is vacant.',
    },
  ),
  assignment(
    'ASG-0008',
    PEOPLE.priya,
    'ROL-PORT',
    SCOPES.comm,
    '2026-08-17',
    '2027-02-28',
    'Access request',
    {
      grantedBy: PEOPLE.nadia,
      grantedAt: '2026-07-28T09:05:00Z',
      justification: 'Commercial portfolio steward for the fare-engine consolidation.',
    },
  ),
  assignment(
    'ASG-0009',
    PEOPLE.anders,
    'ROL-ADMIN',
    SCOPES.enterprise,
    '2024-01-08',
    null,
    'Manual grant',
    {
      grantedBy: PEOPLE.ingrid,
      justification: null,
    },
  ),
  assignment(
    'ASG-0010',
    PEOPLE.priya,
    'ROL-PROD',
    SCOPES.comm,
    '2025-06-02',
    '2026-06-30',
    'Access request',
    {
      grantedBy: PEOPLE.nadia,
      justification: 'Fare-engine discovery, time-boxed to the assessment.',
      lastReviewedAt: '2026-06-12',
    },
  ),
  assignment(
    'ASG-0011',
    PEOPLE.tomas,
    'ROL-ARCH',
    SCOPES.nops,
    '2025-09-01',
    '2026-08-06',
    'Inherited from org unit',
    {
      grantedBy: PEOPLE.julia,
      justification: 'Architecture cover during the Flight Ops re-platform.',
      lastReviewedAt: '2026-06-12',
    },
  ),
  assignment(
    'ASG-0012',
    PEOPLE.louis,
    'ROL-AUDIT',
    SCOPES.enterprise,
    '2026-03-02',
    null,
    'Manual grant',
    {
      grantedBy: PEOPLE.anders,
      grantedAt: '2026-03-02T16:12:00Z',
      justification: 'Requested for the SOC 2 evidence pull.',
      // Revoked because auditor independence and an architect seat conflict —
      // the record stays visible; a revoked grant that disappears is a grant
      // nobody can review.
      revokedAt: '2026-04-18T10:05:00Z',
    },
  ),
  // The CIO's approval authority, which is what DLG-0001 delegates. Without it
  // the register would show her handing over an approval she does not hold —
  // exactly the contradiction these screens exist to make impossible.
  assignment(
    'ASG-0013',
    PEOPLE.ingrid,
    'ROL-GOV',
    SCOPES.enterprise,
    '2024-01-08',
    null,
    'Manual grant',
    {
      grantedBy: PEOPLE.nadia,
      justification: 'Retains approval authority over enterprise governance decisions.',
      lastReviewedAt: '2026-07-03',
    },
  ),
];

function rolesAccessView(): RolesAccessView {
  return {
    asOf: IDENTITY_AS_OF,
    roles: ROLES,
    assignments: ASSIGNMENTS,
    permissionRows: PERMISSION_ROWS,
    openEndedCount: ASSIGNMENTS.filter(
      (row) => row.window.to === null && row.lifecycle.revokedAt === null,
    ).length,
    lastAccessReviewAt: '2026-06-12',
    nextAccessReviewDue: '2026-09-14',
  };
}

// ── Delegation (UC-02-009) ───────────────────────────────────────────────────

const DELEGATIONS: Delegation[] = [
  {
    delegationId: 'DLG-0001',
    authority: 'Approve governance exceptions and definition changes',
    fromPerson: PEOPLE.ingrid,
    toPerson: PEOPLE.nadia,
    scope: SCOPES.enterprise,
    window: { from: '2026-07-20', to: '2026-08-05' },
    lifecycle: { revokedAt: null, requiresAcceptance: true, acceptedAt: '2026-07-18T15:22:00Z' },
    reason: 'Board travel; enterprise approvals must not queue behind one calendar.',
    permissions: ['approve'],
    excluded: [
      'Restricted financial fields — the entitlement is not part of the delegation',
      'Granting or revoking access for others',
    ],
    revokedBy: null,
    notifiedPartyCount: 12,
    auditRef: 'AUD-2026-0714',
    // This repository has no signed-in caller to judge against; the API
    // decides who may end a delegation.
    canRevoke: false,
  },
  {
    delegationId: 'DLG-0002',
    authority: 'Approve product definition changes in Crew Operations',
    fromPerson: PEOPLE.maria,
    toPerson: PEOPLE.simone,
    scope: SCOPES.crew,
    window: { from: '2026-07-27', to: '2026-08-04' },
    lifecycle: { revokedAt: null, requiresAcceptance: true, acceptedAt: '2026-07-26T07:41:00Z' },
    reason: 'Crew ops recovery exercise; Maria is on the operational bridge all week.',
    permissions: ['approve', 'edit'],
    excluded: ['Changes to the Crew Operations value stream definition itself'],
    revokedBy: null,
    notifiedPartyCount: 5,
    auditRef: 'AUD-2026-0731',
    // This repository has no signed-in caller to judge against; the API
    // decides who may end a delegation.
    canRevoke: false,
  },
  {
    delegationId: 'DLG-0003',
    authority: 'Approve network planning portfolio changes',
    fromPerson: PEOPLE.julia,
    toPerson: PEOPLE.tomas,
    scope: SCOPES.network,
    window: { from: '2026-08-10', to: '2026-08-28' },
    lifecycle: { revokedAt: null, requiresAcceptance: true, acceptedAt: '2026-07-29T11:02:00Z' },
    reason: 'Annual leave. Window matches the leave record exactly.',
    permissions: ['approve', 'propose'],
    excluded: ['Approving Tomas’s own proposals — self-approval is refused by the command handler'],
    revokedBy: null,
    notifiedPartyCount: 8,
    auditRef: 'AUD-2026-0729',
    // This repository has no signed-in caller to judge against; the API
    // decides who may end a delegation.
    canRevoke: false,
  },
  {
    delegationId: 'DLG-0004',
    authority: 'Approve application mapping corrections in Flight Operations',
    fromPerson: PEOPLE.julia,
    toPerson: PEOPLE.louis,
    scope: SCOPES.nops,
    window: { from: '2026-08-03', to: '2026-08-21' },
    lifecycle: { revokedAt: null, requiresAcceptance: true, acceptedAt: null },
    reason: 'Re-platform cutover; mapping corrections cannot wait for the weekly forum.',
    permissions: ['approve'],
    excluded: ['Investment and cost fields — Louis holds no finance entitlement'],
    revokedBy: null,
    // The notification job has not run for this one yet. Null, not zero: zero
    // would read as "nobody needed telling", which is a different claim.
    notifiedPartyCount: null,
    auditRef: 'AUD-2026-0730',
    // This repository has no signed-in caller to judge against; the API
    // decides who may end a delegation.
    canRevoke: false,
  },
  {
    delegationId: 'DLG-0005',
    authority: 'Approve commercial portfolio changes',
    fromPerson: PEOPLE.nadia,
    toPerson: PEOPLE.priya,
    scope: SCOPES.comm,
    window: { from: '2026-06-01', to: '2026-06-30' },
    lifecycle: { revokedAt: null, requiresAcceptance: true, acceptedAt: '2026-05-30T09:15:00Z' },
    reason: 'Fare-engine assessment; time-boxed to the assessment window.',
    permissions: ['approve'],
    excluded: ['Anything outside the Commercial department'],
    revokedBy: null,
    notifiedPartyCount: 6,
    auditRef: 'AUD-2026-0601',
    // This repository has no signed-in caller to judge against; the API
    // decides who may end a delegation.
    canRevoke: false,
  },
  {
    delegationId: 'DLG-0006',
    authority: 'Administer connectors and ingestion schedules',
    // From the governance lead rather than the platform administrator: the
    // administrator role is marked non-delegable, and a seed that contradicted
    // its own rule table would undermine the rule table.
    fromPerson: PEOPLE.nadia,
    toPerson: PEOPLE.tomas,
    scope: SCOPES.enterprise,
    window: { from: '2026-07-06', to: '2026-09-30' },
    lifecycle: {
      revokedAt: '2026-07-15T17:48:00Z',
      requiresAcceptance: true,
      acceptedAt: '2026-07-06T08:30:00Z',
    },
    reason: 'Holiday cover for the ingestion platform.',
    permissions: ['administer'],
    excluded: ['Role and access administration'],
    revokedBy: PEOPLE.ingrid,
    notifiedPartyCount: 9,
    auditRef: 'AUD-2026-0715',
    // This repository has no signed-in caller to judge against; the API
    // decides who may end a delegation.
    canRevoke: false,
  },
];

const DELEGATION_RULES: DelegationRule[] = [
  {
    rule: 'A delegation is never wider than the delegator',
    detail:
      'Permissions and scope are intersected with the delegator’s own at the moment the command runs, so a delegation cannot outlive or out-reach the authority it came from.',
    enforcedAt: 'Command handler',
  },
  {
    rule: 'Every delegation ends on a named day',
    detail:
      'There is no open-ended delegation. Anything longer than 90 days is re-granted rather than extended, which forces the reason to be restated.',
    enforcedAt: 'Command handler',
  },
  {
    rule: 'Nobody approves their own proposal',
    detail:
      'Holding a delegated approval does not let the delegate approve work they proposed themselves; the workflow refuses the transition regardless of permission.',
    enforcedAt: 'Approval workflow',
  },
  {
    rule: 'Entitlements do not travel',
    detail:
      'Restricted finance and workforce entitlements are properties of the person, not of the role. A delegate sees masked fields exactly as they would on any other day.',
    enforcedAt: 'Command handler',
  },
  {
    rule: 'Access administration is not delegable',
    detail:
      'Delegating the power to widen access would let authority grow without anyone having been granted the power to grow it.',
    enforcedAt: 'Identity provider',
  },
];

function delegationView(): DelegationView {
  return {
    asOf: IDENTITY_AS_OF,
    delegations: DELEGATIONS,
    rules: DELEGATION_RULES,
    maximumSpanDays: 90,
    // Empty rather than invented: this repository has no signed-in person's
    // grants to read, so it cannot say what they may delegate. The screen
    // treats an empty list as "nothing to delegate", which is the truthful
    // answer here and is replaced by the real one when the API is connected.
    delegatableRoles: [],
    candidates: [],
  };
}

// ── Sign-in (UC-02-005) ──────────────────────────────────────────────────────

const PROVIDERS: IdentityProviderCard[] = [
  {
    id: 'development',
    name: 'Development identity provider',
    protocol: 'In-process, no network call',
    status: 'Active',
    summary:
      'Picks one of six seeded people and hands the app the same claim set production SSO would. It authenticates nothing: choosing an identity here is a statement about who you want to be, not a proof of who you are.',
    facts: [
      { label: 'Credentials collected', value: 'None. There is no password field on this screen.' },
      { label: 'Tokens issued', value: 'None. The session is an in-memory object.' },
      { label: 'Available in', value: 'Development and demo builds only.' },
    ],
  },
  {
    id: 'sso',
    name: 'Aurora corporate SSO',
    protocol: 'OpenID Connect · authorization code with PKCE',
    status: 'NotConfigured',
    summary:
      'In production the app never sees a credential: the browser is redirected to the corporate identity provider, and what comes back is a signed token the app maps to the same fields the development provider fills in by hand.',
    facts: [
      { label: 'Discovery document', value: 'Not configured in this environment.' },
      { label: 'Multi-factor', value: 'Enforced by the identity provider, not by this app.' },
      { label: 'Session lifetime', value: 'Set by the identity provider’s policy.' },
    ],
  },
];

const CLAIMS: ClaimMapping[] = [
  {
    claim: 'sub',
    mapsTo: 'session.currentUser.id',
    required: true,
    developmentSource: 'The seeded person’s identifier.',
  },
  {
    claim: 'name',
    mapsTo: 'session.currentUser.displayName',
    required: true,
    developmentSource: 'The seeded person’s name.',
  },
  {
    claim: 'aurora_scope',
    mapsTo: 'session.currentUser.scopeId · scopeType',
    required: true,
    developmentSource: 'The scope node attached to the seeded person.',
  },
  {
    claim: 'aurora_roles',
    mapsTo: 'session.allowedModules',
    required: true,
    developmentSource: 'The module list on the seeded person.',
  },
  {
    claim: 'aurora_entitlements',
    mapsTo: 'canSeeRestrictedFinance · canSeeWorkforce',
    required: false,
    developmentSource: 'Two booleans on the seeded person.',
  },
  {
    claim: 'amr',
    mapsTo: 'Displayed only. Never used to authorise.',
    required: false,
    developmentSource: 'Absent — the development provider performs no authentication.',
  },
];

function signInView(user: SeededPersona): SignInView {
  return {
    providers: PROVIDERS,
    claims: CLAIMS,
    sessionFacts: [
      { label: 'Provider in use', value: 'Development identity provider' },
      { label: 'Identity', value: `${user.displayName} · ${user.roleLabel}` },
      { label: 'Span of control', value: user.scopeLabel },
      {
        label: 'Authentication method',
        value: 'None performed',
        caveat: 'The development provider asserts an identity. It does not verify one.',
      },
      {
        label: 'Session expiry',
        value: 'Ends when the tab is closed',
        caveat: 'There is no refresh token and nothing is persisted to storage.',
      },
    ],
  };
}

// ── Profile (UC-02-005) ──────────────────────────────────────────────────────

/** Field lists mirror `FINANCE_MASKED_FIELDS` / `WORKFORCE_MASKED_FIELDS`. */
function entitlementsFor(user: SeededPersona): EntitlementRow[] {
  return [
    {
      label: 'Restricted financial fields',
      granted: user.canSeeRestrictedFinance,
      reason: user.canSeeRestrictedFinance
        ? 'Granted with the role. Cost, investment and benefit figures are returned in full.'
        : 'Not granted. Cost and benefit fields come back empty with a reason attached — they are never returned as zero, because a zero would understate spend to exactly the readers least able to tell.',
      maskedFields: user.canSeeRestrictedFinance
        ? []
        : ['annualRunCost', 'totalCostOfOwnership', 'investmentYtd', 'businessValue'],
    },
    {
      label: 'Workforce data',
      granted: user.canSeeWorkforce,
      reason: user.canSeeWorkforce
        ? 'Granted. Contractor mix, vacancies and team capacity are visible.'
        : 'Not granted. Contractor headcount, vacancies and capacity are withheld; team totals still show the internal figure so the rows are not silently short.',
      maskedFields: user.canSeeWorkforce
        ? []
        : ['contractorFte', 'vacancyCount', 'capacityPerIteration'],
    },
    {
      label: 'Governance administration',
      granted: user.allowedModules.includes('governance'),
      reason: user.allowedModules.includes('governance')
        ? 'Granted. Roles, access windows and delegations can be changed within your span.'
        : 'Not granted. Everything on the access and delegation screens is readable and none of it is changeable by you.',
      maskedFields: [],
    },
  ];
}

const SIGN_IN_ACTIVITY: SignInEvent[] = [
  {
    at: '2026-07-31T04:18:00Z',
    provider: 'Development identity provider',
    method: 'Identity selected',
    outcome: 'Succeeded',
    client: 'Aurora web · glass shell',
  },
  {
    at: '2026-07-30T06:52:00Z',
    provider: 'Development identity provider',
    method: 'Identity selected',
    outcome: 'Succeeded',
    client: 'Aurora web · glass shell',
  },
  {
    at: '2026-07-29T17:40:00Z',
    provider: 'Development identity provider',
    method: 'Session ended',
    outcome: 'Ended',
    client: 'Aurora web · glass shell',
  },
  {
    at: '2026-07-29T06:31:00Z',
    provider: 'Development identity provider',
    method: 'Identity selected',
    outcome: 'Succeeded',
    client: 'Aurora web · glass shell',
  },
];

function profileFor(user: SeededPersona): ProfileView {
  // The seeded personas are keyed by business code in the seed; the picker
  // keys them by uuid. Matching on the display name keeps the two fixtures
  // aligned without either one importing the other's identifiers.
  const known = Object.values(PEOPLE).find((entry) => entry.displayName === user.displayName);
  const self: PersonRef = known ?? {
    id: user.id,
    code: 'PPL-????',
    displayName: user.displayName,
    jobTitle: user.roleLabel,
    orgUnitLabel: user.scopeLabel,
  };

  return {
    asOf: IDENTITY_AS_OF,
    person: self,
    personaId: user.personaId,
    roleLabel: user.roleLabel,
    scope: { code: user.scopeCode, label: user.scopeLabel, type: user.scopeType } as ScopeRef,
    entitlements: entitlementsFor(user),
    modules: ALL_MODULES.map((module) => ({
      module,
      label: MODULE_LABELS[module] ?? module,
      allowed: user.allowedModules.includes(module),
    })),
    assignments: ASSIGNMENTS.filter((row) => row.person.code === self.code),
    delegationsGranted: DELEGATIONS.filter((row) => row.fromPerson.code === self.code),
    delegationsHeld: DELEGATIONS.filter((row) => row.toPerson.code === self.code),
    signInActivity: SIGN_IN_ACTIVITY,
    // True when the fixture has no person matching the signed-in identity.
    isUnmatchedIdentity: known === undefined,
  };
}

// ── Envelope ─────────────────────────────────────────────────────────────────

/**
 * The response envelope these screens would receive from an identity endpoint.
 *
 * Built from the signed-in user rather than hard-coded, so the read-only state,
 * the masked-field list and the scope banner all move when the top bar's
 * identity moves — which is the property the whole authorization model is
 * demonstrated by.
 */
function identityMetadata(user: SeededPersona, correlationId: string): QueryMetadata {
  const canAdminister = user.allowedModules.includes('governance');
  const maskedFields = [
    ...(user.canSeeRestrictedFinance ? [] : ['investmentYtd', 'annualRunCost']),
    ...(user.canSeeWorkforce ? [] : ['contractorFte', 'vacancyCount']),
  ];

  return {
    correlationId,
    periodStart: '2026-01-01',
    periodEnd: IDENTITY_AS_OF,
    comparisonPeriodStart: null,
    comparisonPeriodEnd: null,
    asOf: null,
    sourceStatus: {
      overall: 'Available',
      refreshedAt: REFRESHED_AT,
      lastGoodAt: null,
      failedRunAt: null,
      contributingSources: [
        {
          sourceSystem: 'Workday HCM',
          status: 'Available',
          refreshedAt: REFRESHED_AT,
          recordCount: 4820,
        },
        {
          sourceSystem: 'Entra ID',
          status: 'Available',
          refreshedAt: REFRESHED_AT,
          recordCount: 4791,
        },
      ],
    },
    quality: {
      score: 94,
      completeness: 0.97,
      validity: 0.99,
      uniqueness: 1,
      consistency: 0.93,
      timeliness: 0.98,
      openIssueCount: 2,
    },
    confidence: 'High',
    scope: {
      scopeId: user.scopeId,
      scopeType: user.scopeType,
      scopeLabel: user.scopeLabel,
      isNarrowed: user.scopeType !== 'Enterprise',
      maskedFields,
    },
    allowedActions: [
      {
        action: 'access.administer',
        permission: 'administer',
        enabled: canAdminister,
        disabledReason: canAdminister
          ? null
          : 'Your role carries observation rights over access, not administration. Everything here is readable and nothing here is changeable by you.',
      },
      {
        action: 'access.export',
        permission: 'export',
        enabled: false,
        disabledReason:
          'Export is not wired to this screen yet — there is no identity endpoint behind it to export from.',
      },
    ],
    isSyntheticData: true,
  };
}

// ── Repository ───────────────────────────────────────────────────────────────

export interface IdentityRepository {
  getRolesAccess(): Promise<QueryResult<RolesAccessView>>;
  getDelegation(): Promise<QueryResult<DelegationView>>;
  getSignIn(): Promise<QueryResult<SignInView>>;
  getProfile(): Promise<QueryResult<ProfileView>>;
}

/**
 * The four identity projections, over the signed-in user from the context.
 *
 * The user is taken from `RepositoryContext` rather than passed per call for the
 * same reason every other repository here does it: the API client that replaces
 * this one will authorise from the token it already holds, and a screen that had
 * to hand its own identity to the data layer would be a screen that could hand
 * the wrong one.
 */
export function createIdentityRepository(context: RepositoryContext): IdentityRepository {
  const { user } = context;

  return {
    getRolesAccess() {
      return resolved({
        data: rolesAccessView(),
        meta: identityMetadata(user, 'identity-roles'),
      });
    },
    getDelegation() {
      return resolved({
        data: delegationView(),
        meta: identityMetadata(user, 'identity-delegation'),
      });
    },
    getSignIn() {
      return resolved({
        data: signInView(user),
        meta: identityMetadata(user, 'identity-signin'),
      });
    },
    getProfile() {
      return resolved({
        data: profileFor(user),
        meta: identityMetadata(user, 'identity-profile'),
      });
    },
  };
}
